import React, { useEffect, useMemo, useState, useCallback, useContext, useRef } from 'react';
import axios from 'axios';
import MetricsPanel from '../components/presence/MetricsPanel';
import MetricsCharts from '../components/presence/MetricsCharts';
import PlayerTable from '../components/presence/PlayerTable';
import SessionTable from '../components/presence/SessionTable';
import { SocketContext } from '../context/socket';
import { SOCKET_URL } from '../socketConfig';

const REFRESH_MS = 4000;
const GUEST_PRUNE_MS = 5 * 60 * 1000; // keep departed guests visible for 5 minutes
const GUEST_SEEN_STORAGE_KEY = 'presenceAdmin_guest_seen';
const GUEST_TOTAL_STORAGE_KEY = 'presenceAdmin_guest_total';
const GUEST_SEEN_24H_KEY = 'presenceAdmin_guest_seen_24h';
const WINDOW_24H = 24 * 60 * 60 * 1000;

const nowTs = () => Date.now();

function normalizeMetrics(payload = {}) {
  return {
    snapshotCount: payload.snapshotCount || 0,
    diffCount: payload.diffCount || 0,
    heartbeatSweeps: payload.heartbeatSweeps || 0,
    activeSessions: payload.activeSessions || 0,
    activePlayers: payload.activePlayers || 0,
    latencyAvg: payload.latencyAvg || payload.latencyMs || null,
    diffStats: payload.diffStats || { add: 0, update: 0, remove: 0 },
    memoryUsage: payload.memoryUsage || null,
    diffTimeMs: payload.diffTimeMs || null,
    sweepMs: payload.sweepMs || null,
  };
}

function PresenceAdmin() {
  const { socket } = useContext(SocketContext) || {};
  const [metrics, setMetrics] = useState(() => normalizeMetrics());
  const [players, setPlayers] = useState({});
  const [sessions, setSessions] = useState({});
  const [guestSessions, setGuestSessions] = useState({});
  const [guestCount, setGuestCount] = useState(0);
  const [guestTotal, setGuestTotal] = useState(0);
  const [guest24h, setGuest24h] = useState(0);
  const [guestExpanded, setGuestExpanded] = useState({});
  const [latencySeries, setLatencySeries] = useState([]);
  const [sweepSeries, setSweepSeries] = useState([]);
  const [diffSeries, setDiffSeries] = useState([]);
  const guestSeenRef = useRef(new Set());
  const guest24hRef = useRef(new Map()); // sessionId -> firstSeenAt

  const presenceDebugUrls = useMemo(() => {
    const useLocal =
      String(process.env.REACT_APP_USE_LOCAL_SOCKET || '').toLowerCase() ===
      'true';
    const localPort = process.env.REACT_APP_LOCAL_SOCKET_PORT || '8900';
    if (useLocal) {
      const httpBase = `http://localhost:${localPort}`;
      return [`${httpBase}/presence/debug`, `${httpBase}/presence/debug`];
    }
    const envUrl = process.env.REACT_APP_SOCKET_URL || '';
    const base = envUrl || SOCKET_URL || '';
    const httpBase = base.replace(/^ws/i, 'http').replace(/\/$/, '');
    return [`${httpBase}/presence/debug`, `${httpBase}/presence/debug`];
  }, []);

  const logDev = useCallback((label, payload) => {
    const dbg =
      String(process.env.REACT_APP_PRESENCE_DEBUG || '').toLowerCase() ===
      'true';
    if (dbg) {
      // eslint-disable-next-line no-console
      console.log(label, payload);
    }
  }, []);

  const upsertPlayer = useCallback((playerId, update) => {
    setPlayers((prev) => {
      const next = { ...prev };
      const existing = next[playerId] || {};
      next[playerId] = { ...existing, ...update };
      return next;
    });
  }, []);

  const upsertSession = useCallback((sessionId, update) => {
    setSessions((prev) => {
      const next = { ...prev };
      const existing = next[sessionId] || {};
      next[sessionId] = { ...existing, ...update };
      return next;
    });
  }, []);

  const removeSession = useCallback((sessionId) => {
    setSessions((prev) => {
      if (!prev[sessionId]) return prev;
      const next = { ...prev };
      delete next[sessionId];
      return next;
    });
  }, []);

  const applySnapshotPayload = useCallback((snapshot) => {
    if (!snapshot) return;

    const sessionSummary = snapshot.sessionSummary || snapshot.sessions;
    const sessionEntries =
      sessionSummary && typeof sessionSummary === 'object'
        ? Object.entries(sessionSummary)
        : Array.isArray(sessionSummary)
        ? sessionSummary.map((val) => [val.sessionId || val.id, val])
        : [];

    if (sessionEntries.length) {
      setSessions(() => {
        const next = {};
        sessionEntries.forEach(([sid, val]) => {
          if (!sid) return;
          next[sid] = { sessionId: sid, ...(val || {}) };
        });
        return next;
      });
    }

    const playerSummary = snapshot.playerSummary || snapshot.players;
    const playerEntries =
      playerSummary && typeof playerSummary === 'object'
        ? Object.entries(playerSummary)
        : Array.isArray(playerSummary)
        ? playerSummary.map((val) => [val.playerId || val._id, val])
        : [];

    if (playerEntries.length) {
      setPlayers(() => {
        const next = {};
        playerEntries.forEach(([pid, val]) => {
          if (!pid) return;
          next[pid] = { playerId: pid, ...(val || {}) };
        });
        return next;
      });
    }
  }, []);

  const applyDiffPayload = useCallback((diff) => {
    if (!diff) return;
    const toArray = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === 'object') return Object.values(val);
      return [];
    };

    const add = toArray(diff.add || diff.joins);
    const update = toArray(diff.update || diff.updates);
    const remove = toArray(diff.remove || diff.leaves);

    add.forEach((s) => {
      if (!s) return;
      const sid = s.sessionId || s.id || s.session;
      if (sid) upsertSession(sid, { sessionId: sid, ...s });
    });
    update.forEach((s) => {
      if (!s) return;
      const sid = s.sessionId || s.id || s.session;
      if (sid) upsertSession(sid, { sessionId: sid, ...s });
    });
    remove.forEach((s) => {
      const sid = s?.sessionId || s?.id || s;
      if (sid) removeSession(sid);
    });
  }, [removeSession, upsertSession]);

  const applyGuestSnapshot = useCallback((snapshot) => {
    const guests = snapshot?.guests || snapshot?.sessions || {};
    const entries = Array.isArray(guests)
      ? guests.map((val) => ({
          ...(val || {}),
          sessionId:
            val?.sessionId ||
            val?.session_id ||
            val?.guestId ||
            val?._id ||
            null,
        }))
      : Object.entries(guests).map(([sid, val]) => ({
          ...(val || {}),
          sessionId:
            val?.sessionId || val?.session_id || val?.guestId || val?._id || sid,
        }));
    if (!entries.length) {
      setGuestSessions((prev) => {
        const kept = { ...prev };
        const now = Date.now();
        Object.entries(kept).forEach(([sid, g]) => {
          const removedAt = g.removedAt || (g.activityType === 'left' ? g.updatedAt : null);
          if (g.activityType === 'left' && removedAt && now - removedAt > GUEST_PRUNE_MS) {
            delete kept[sid];
          }
        });
        return kept;
      });
      setGuestCount(snapshot?.count || 0);
      return;
    }
    const now = Date.now();
    const next = {};
    entries.forEach((g) => {
      if (!g?.sessionId) return;
      next[g.sessionId] = {
        ...g,
        sessionId: g.sessionId,
      };
      if (!guestSeenRef.current.has(g.sessionId)) {
        guestSeenRef.current.add(g.sessionId);
        guest24hRef.current.set(g.sessionId, Date.now());
      }
    });
    setGuestSessions((prev) => {
      // Keep recent "left" entries that aren't expired
      Object.entries(prev || {}).forEach(([sid, g]) => {
        const removedAt = g.removedAt || (g.activityType === 'left' ? g.updatedAt : null);
        if (g.activityType === 'left' && removedAt && now - removedAt <= GUEST_PRUNE_MS) {
          next[sid] = next[sid] || g;
        }
      });
      return next;
    });
    const nowTs = Date.now();
    // prune 24h map
    guest24hRef.current.forEach((seenAt, sid) => {
      if (nowTs - seenAt > WINDOW_24H) guest24hRef.current.delete(sid);
    });
    setGuestTotal(guestSeenRef.current.size);
    setGuest24h(guest24hRef.current.size);
    try {
      localStorage.setItem(
        GUEST_SEEN_STORAGE_KEY,
        JSON.stringify(Array.from(guestSeenRef.current)),
      );
      localStorage.setItem(
        GUEST_TOTAL_STORAGE_KEY,
        String(guestSeenRef.current.size),
      );
      localStorage.setItem(
        GUEST_SEEN_24H_KEY,
        JSON.stringify(Array.from(guest24hRef.current.entries())),
      );
    } catch {
      /* ignore storage failures */
    }
    setGuestCount(snapshot?.count || entries.length);
  }, []);

  const applyGuestDiff = useCallback((diff) => {
    if (!diff) return;
    const addRaw = diff.add || {};
    const updateRaw = diff.update || {};

    const normalizeList = (input) => {
      if (Array.isArray(input)) {
        return input.map((val) => ({
          ...(val || {}),
          sessionId: val?.sessionId || val?.session_id || val?.guestId || val?._id,
        }));
      }
      return Object.entries(input).map(([sid, val]) => ({
        ...(val || {}),
        sessionId: val?.sessionId || val?.session_id || sid,
      }));
    };

    const add = normalizeList(addRaw);
    const update = normalizeList(updateRaw);
    const remove = diff.remove || [];
    const count = diff.count;

    if (add.length || update.length || remove.length) {
      setGuestSessions((prev) => {
        const now = Date.now();
        const next = { ...prev };

        const prune = () => {
          Object.entries(next).forEach(([sid, g]) => {
            const removedAt = g.removedAt || (g.activityType === 'left' ? g.updatedAt : null);
            if (g.activityType === 'left' && removedAt && now - removedAt > GUEST_PRUNE_MS) {
              delete next[sid];
            }
          });
        };

        add.forEach((g) => {
          if (!g?.sessionId) return;
          if (!guestSeenRef.current.has(g.sessionId)) {
            guestSeenRef.current.add(g.sessionId);
            guest24hRef.current.set(g.sessionId, now);
          }
          next[g.sessionId] = { ...next[g.sessionId], ...g };
        });

        update.forEach((g) => {
          if (!g?.sessionId) return;
          next[g.sessionId] = { ...next[g.sessionId], ...g };
        });

        remove.forEach((sid) => {
          if (!sid) return;
          const entry = next[sid] || { sessionId: sid };
          next[sid] = {
            ...entry,
            activityType: 'left',
            removedAt: entry.removedAt || now,
            updatedAt: now,
          };
        });

        prune();
        return next;
      });
    }

    if (typeof count === 'number') setGuestCount(count);
    else setGuestCount((prev) => Math.max(0, prev + add.length - remove.length));

    // Persist counts after updates
    const nowTs = Date.now();
    guest24hRef.current.forEach((seenAt, sid) => {
      if (nowTs - seenAt > WINDOW_24H) guest24hRef.current.delete(sid);
    });
    setGuestTotal(guestSeenRef.current.size);
    setGuest24h(guest24hRef.current.size);
    try {
      localStorage.setItem(
        GUEST_SEEN_STORAGE_KEY,
        JSON.stringify(Array.from(guestSeenRef.current)),
      );
      localStorage.setItem(
        GUEST_TOTAL_STORAGE_KEY,
        String(guestSeenRef.current.size),
      );
      localStorage.setItem(
        GUEST_SEEN_24H_KEY,
        JSON.stringify(Array.from(guest24hRef.current.entries())),
      );
    } catch {
      /* ignore storage failures */
    }
  }, []);

  const fetchDebug = useCallback(async () => {
    const targets = presenceDebugUrls;
    let lastErr;
    for (const url of targets) {
      try {
        const { data } = await axios.get(url, { withCredentials: true });
        const payload = data?.data || data || {};
        const nextMetrics = normalizeMetrics(payload);
        // Merge metrics only if payload has them
        setMetrics((prev) => ({ ...prev, ...nextMetrics }));
        applySnapshotPayload(payload);
        if (payload?.guests || payload?.guestSessions || payload?.guestSnapshot) {
          applyGuestSnapshot(
            payload.guestSnapshot || {
              guests: payload.guests || payload.guestSessions || {},
              count:
                payload.guestCount ||
                payload.guestsCount ||
                payload.count ||
                undefined,
            },
          );
        }
        logDev('[presence-admin] metrics update', nextMetrics);
        return;
      } catch (err) {
        lastErr = err;
        continue;
      }
    }
    if (lastErr) logDev('[presence-admin] fetch debug failed', lastErr?.message || lastErr);
  }, [applySnapshotPayload, logDev, presenceDebugUrls]);

  // REST polling
  useEffect(() => {
    fetchDebug();
    const id = setInterval(fetchDebug, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchDebug]);

  // Socket listeners
  useEffect(() => {
    if (!socket) return undefined;

    // hydrate running total from storage once when socket is ready
    try {
      const stored = localStorage.getItem(GUEST_SEEN_STORAGE_KEY);
      if (stored) {
        const arr = JSON.parse(stored);
        if (Array.isArray(arr)) {
          guestSeenRef.current = new Set(arr);
          setGuestTotal(guestSeenRef.current.size);
        }
      }
      const storedTotal = localStorage.getItem(GUEST_TOTAL_STORAGE_KEY);
      if (storedTotal && !guestSeenRef.current.size) {
        const n = Number(storedTotal);
        if (!Number.isNaN(n)) setGuestTotal(n);
      }
      const stored24h = localStorage.getItem(GUEST_SEEN_24H_KEY);
      if (stored24h) {
        const entries = JSON.parse(stored24h);
        if (Array.isArray(entries)) {
          const now = Date.now();
          entries.forEach(([sid, ts]) => {
            if (sid && ts && now - ts <= WINDOW_24H) {
              guest24hRef.current.set(sid, ts);
            }
          });
          setGuest24h(guest24hRef.current.size);
        }
      }
    } catch {
      /* ignore storage issues */
    }

    const requestGuestSnapshot = () => {
      socket.emit('guest:snapshot:request');
    };

    const onGuestSnapshot = (payload) => {
      // Always log guest snapshots on this admin page for visibility.
      // eslint-disable-next-line no-console
      console.log('[guest:snapshot]', payload);
      logDev('[guest:snapshot]', payload);
      applyGuestSnapshot(payload);
    };

    const onGuestDiff = (payload) => {
      // eslint-disable-next-line no-console
      console.log('[guest:diff]', payload);
      logDev('[guest:diff]', payload);
      applyGuestDiff(payload);
    };

    const onGuestCount = (payload) => {
      if (typeof payload?.count === 'number') setGuestCount(payload.count);
    };

    const guestTap = (event, payload) => {
      if (typeof event === 'string' && event.startsWith('guest:')) {
        // eslint-disable-next-line no-console
        console.log('[guest:any]', event, payload);
      }
    };

    const handleMetrics = (payload) => {
      const nextMetrics = normalizeMetrics(payload);
      setMetrics(nextMetrics);
      setLatencySeries((prev) =>
        [...prev, { t: nowTs(), v: nextMetrics.latencyAvg || 0 }].slice(-30),
      );
      setSweepSeries((prev) =>
        [...prev, { t: nowTs(), v: nextMetrics.sweepMs || 0 }].slice(-30),
      );
      setDiffSeries((prev) =>
        [...prev, { t: nowTs(), v: nextMetrics.diffStats?.update || 0 }].slice(-30),
      );
      logDev('[presence-admin] metrics update', payload);
    };

    const handlePlayerStatus = (update) => {
      if (!update || !update.playerId) return;
      upsertPlayer(update.playerId, update);
      logDev('[presence-admin] player update', update);
    };

    const handleSnapshot = (snapshot) => {
      applySnapshotPayload(snapshot);
      logDev('[presence-admin] snapshot', snapshot);
    };

    const handleDiff = (diff) => {
      applyDiffPayload(diff);
      logDev('[presence-admin] session diff', diff);
    };

    socket.on('presence:metrics', handleMetrics);
    socket.on('player:status', handlePlayerStatus);
    socket.on('presence:snapshot', handleSnapshot);
    socket.on('presence:diff', handleDiff);
    socket.on('guest:snapshot', onGuestSnapshot);
    socket.on('guest:diff', onGuestDiff);
    socket.on('guest:count', onGuestCount);
    socket.onAny(guestTap);
    socket.on('connect', requestGuestSnapshot);

    // Ask for a guest snapshot on mount
    requestGuestSnapshot();

    return () => {
      socket.off('presence:metrics', handleMetrics);
      socket.off('player:status', handlePlayerStatus);
      socket.off('presence:snapshot', handleSnapshot);
      socket.off('presence:diff', handleDiff);
      socket.off('guest:snapshot', onGuestSnapshot);
      socket.off('guest:diff', onGuestDiff);
      socket.off('guest:count', onGuestCount);
      socket.offAny(guestTap);
      socket.off('connect', requestGuestSnapshot);
    };
  }, [
    socket,
    applyDiffPayload,
    applySnapshotPayload,
    applyGuestDiff,
    applyGuestSnapshot,
    logDev,
    upsertPlayer,
  ]);

  const playerRows = useMemo(() => Object.values(players || {}), [players]);
  const sessionRows = useMemo(() => Object.values(sessions || {}), [sessions]);
  const playerRowsEnriched = useMemo(() => {
    const counts = {};
    const lastSeenMap = {};
    sessionRows.forEach((s) => {
      const pid = s.playerId;
      if (!pid) return;
      counts[pid] = (counts[pid] || 0) + 1;
      const ts = s.updatedAt || s.timestamp || s.lastSeen;
      if (ts && (!lastSeenMap[pid] || ts > lastSeenMap[pid])) {
        lastSeenMap[pid] = ts;
      }
    });
    return playerRows.map((p) => {
      const pid = p.playerId || p._id;
      const sessionCount =
        p.sessionCount ??
        p.sessions ??
        counts[pid] ??
        0;
      const lastSeen =
        p.lastSeen ??
        p.timestamp ??
        p.updatedAt ??
        p.lastSeenAt ??
        lastSeenMap[pid] ??
        null;
      return {
        ...p,
        sessions: sessionCount,
        sessionCount,
        lastSeen,
      };
    });
  }, [playerRows, sessionRows]);

  const displayMetrics = useMemo(() => {
    const sessionCount = sessionRows.length;
    const playerCount = playerRowsEnriched.length;
    return {
      ...metrics,
      activeSessions: Math.max(metrics.activeSessions || 0, sessionCount),
      activePlayers: Math.max(metrics.activePlayers || 0, playerCount),
      guestCount,
    };
  }, [guestCount, metrics, playerRowsEnriched, sessionRows]);

  const guestRows = useMemo(() => {
    const pickSource = (g = {}) => {
      const utm = g.utm || {};
      const query = g.query || {};
      return (
        g.utmSource ||
        utm.utmSource ||
        query.utmSource ||
        g.utm_source ||
        utm.utm_source ||
        query.utm_source ||
        utm.utm_source ||
        g.utmMedium ||
        utm.utmMedium ||
        query.utmMedium ||
        g.utm_medium ||
        utm.utm_medium ||
        query.utm_medium ||
        g.utmCampaign ||
        utm.utmCampaign ||
        query.utmCampaign ||
        g.utm_campaign ||
        utm.utm_campaign ||
        query.utm_campaign ||
        g.utmContent ||
        utm.utmContent ||
        query.utmContent ||
        g.utm_content ||
        utm.utm_content ||
        query.utm_content ||
        utm.source ||
        query.source ||
        utm.medium ||
        query.medium ||
        utm.campaign ||
        query.campaign ||
        utm.content ||
        query.content ||
        g.source ||
        query.referrer ||
        g.referrer ||
        g.origin ||
        'n/a'
      );
    };

    const pickLatency = (g = {}) =>
      g.latencyMs ??
      g.latency_ms ??
      g.latency ??
      g.network?.latencyMs ??
      null;

    const vals = Object.values(guestSessions || {});
    return vals
      .map((g) => {
        const source = pickSource(g);
        const latency = pickLatency(g);
        return { ...g, __source: source, __latency: latency };
      })
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  }, [guestSessions]);

  return (
    <div className="container-fluid py-4">
      <h2 className="mb-3">Presence Observatory (V1)</h2>
      <MetricsPanel metrics={displayMetrics} />
      <MetricsCharts
        latencySeries={latencySeries}
        sweepSeries={sweepSeries}
        diffSeries={diffSeries}
      />
      <div className="card mt-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex flex-column">
            <span>Guests</span>
            <small className="text-muted">
              Live: {guestCount} • 24h: {guest24h} • All-time: {guestTotal}
            </small>
            <button
              type="button"
              className="btn btn-link p-0 mt-1"
              style={{ fontSize: 12 }}
              onClick={() => {
                guestSeenRef.current.clear();
                guest24hRef.current.clear();
                setGuestTotal(0);
                setGuest24h(0);
                try {
                  localStorage.removeItem(GUEST_SEEN_STORAGE_KEY);
                  localStorage.removeItem(GUEST_TOTAL_STORAGE_KEY);
                  localStorage.removeItem(GUEST_SEEN_24H_KEY);
                } catch {
                  /* ignore */
                }
              }}
            >
              Reset total
            </button>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th scope="col">Session</th>
                <th scope="col" style={{ width: 140 }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {guestRows.slice(0, 50).map((g) => (
                <React.Fragment key={g.sessionId}>
                  <tr>
                    <td className="text-monospace small">{g.sessionId}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0"
                        onClick={() =>
                          setGuestExpanded((prev) => ({
                            ...prev,
                            [g.sessionId]: !prev[g.sessionId],
                          }))
                        }
                      >
                        {guestExpanded[g.sessionId] ? 'Hide details' : 'Show details'}
                      </button>
                    </td>
                  </tr>
                  {guestExpanded[g.sessionId] && (
                    <tr>
                      <td colSpan="2" className="small bg-light">
                        <div className="d-flex flex-column flex-md-row gap-3">
                          <div>
                            <strong>Updated:</strong>{' '}
                            {g.updatedAt
                              ? new Date(g.updatedAt).toLocaleTimeString()
                              : 'n/a'}
                          </div>
                          <div>
                            <strong>Referrer:</strong>{' '}
                            {g.referrer ||
                              g.query?.referrer ||
                              g.origin ||
                              'n/a'}
                          </div>
                          <div>
                            <strong>Source:</strong> {g.__source || 'n/a'}
                          </div>
                          <div>
                            <strong>Region:</strong> {g.region || 'unknown'}
                          </div>
                          <div>
                            <strong>Activity:</strong>{' '}
                            {g.activityType || 'active'}
                          </div>
                          <div>
                            <strong>Device:</strong> {g.device || 'n/a'}
                          </div>
                          <div>
                            <strong>Latency:</strong>{' '}
                            {g.__latency != null ? `${g.__latency} ms` : 'n/a'}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {!guestRows.length && (
                <tr>
                  <td colSpan="2" className="text-center text-muted">
                    No guest sessions.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row g-3 mt-2">
        <div className="col-12 col-md-6" style={{ minWidth: 0 }}>
          <PlayerTable rows={playerRowsEnriched} />
        </div>
        <div className="col-12 col-md-6" style={{ minWidth: 0 }}>
          <SessionTable rows={sessionRows} />
        </div>
      </div>
    </div>
  );
}

export default PresenceAdmin;
